import { formatHesaSignals } from "@/lib/hesaSignals";
import { TrendingUp } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

/**
 * Compact outcomes-signals strip. Two rendering paths:
 *   1) `stats` prop  → auto-formats HESA/Discover Uni fields (uk directory)
 *   2) `signals` prop → renders a pre-computed pill array (editorial outcomes)
 *
 * A pill may carry a `progression` payload — currently used by the starting-
 * salary pill to show a 3-year salary trajectory. When present, the pill is
 * wrapped in a Radix tooltip that reveals `$52k → $73k @ 3y  · +40%` on hover,
 * with a small trend icon appended to the label as an affordance.
 *
 * Only pills whose sample size clears the confidence threshold are surfaced
 * (thresholds live in `hesaSignals.js`). Renders nothing when the array is
 * empty so the caller never needs to gate the section on data availability.
 */
export default function HesaSignalsStrip({
  stats = null,
  signals: preComputed = null,
  className = "",
  testIdPrefix = "",
}) {
  const signals = preComputed ?? formatHesaSignals(stats);
  if (!signals || signals.length === 0) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid={testIdPrefix ? `${testIdPrefix}-signals` : "hesa-signals"}
        className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500 ${className}`}
      >
        {signals.map((s, i) => {
          const testId = testIdPrefix ? `${testIdPrefix}-signal-${s.key}` : `hesa-signal-${s.key}`;
          const inner = (
            <span data-testid={testId} className="inline-flex items-center">
              {i > 0 && <span className="mr-2 text-stone-300">·</span>}
              <span className="text-stone-700">{s.label}</span>
              {s.progression && (
                <TrendingUp className="ml-1 h-2.5 w-2.5 text-[#4C1D95]" aria-hidden="true" />
              )}
            </span>
          );
          if (s.progression) {
            return (
              <Tooltip key={s.key}>
                <TooltipTrigger asChild>
                  <span
                    className="cursor-help border-b border-dotted border-stone-400"
                    data-testid={`${testId}-progression-trigger`}
                  >
                    {inner}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="bg-stone-900 text-white text-[11px] font-mono normal-case tracking-normal px-3 py-2 max-w-[240px]"
                  data-testid={`${testId}-progression-tooltip`}
                >
                  <div className="flex items-center gap-1.5 font-medium">
                    <span>{s.progression.startLabel}</span>
                    <span className="text-stone-400">→</span>
                    <span>{s.progression.endLabel}</span>
                    <span className="text-emerald-400 ml-0.5">{s.progression.yearLabel || "@ 3y"}</span>
                  </div>
                  <div className="mt-1 text-stone-300 text-[10px]">
                    +{s.progression.growthPct}% growth over that period.
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          }
          return (
            <span key={s.key} title={s.title}>{inner}</span>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
